import { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { apiService } from '../services/api';

const money = (v?: number | null) => (v == null ? '–' : `$${Math.round(v).toLocaleString()}`);

/** Tax and fund-domicile check for an Israeli investor (Feature 54). `version` reloads it when holdings change. */
export default function TaxCheck({ version }: { version?: unknown }) {
  const [d, setD] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setD(null);
    apiService.getTaxCheck().then(setD).catch(e => setErr(e?.response?.data?.detail || e?.message || 'Failed'));
  }, [version]);
  if (err) return <Alert severity="warning" sx={{ mb: 2 }}>Tax check: {err}</Alert>;
  if (!d) return <Paper sx={{ p: 2, mb: 2 }}><CircularProgress size={20} /></Paper>;
  if (!d.rows) return <Alert severity="info" sx={{ mb: 2 }}>Tax and fund domicile check: {d.note}</Alert>;
  const e = d.estate, dv = d.dividends, h = d.harvest;
  const withAlt = d.rows.filter((r: any) => r.alternatives?.length);
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>Tax and fund domicile — money you keep for certain</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 950 }}>
        Rules for an Israeli resident who is not a US person, as published; not tax advice — confirm with an adviser who
        knows both countries before acting.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">US estate tax</Typography>
            <Typography variant="h6" fontWeight={700} color={e.estimated_tax > 0 ? 'error.main' : 'success.main'}>
              {e.estimated_tax > 0 ? `${money(e.estimated_tax)} at risk` : 'Under the $60,000 line'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {money(e.us_situs_value)} in US-listed shares and US funds. Above $60,000 a non-US person's heirs owe US estate
              tax up to 40%; Israel has no estate tax treaty with the US. Ireland-domiciled (UCITS) funds holding the same
              stocks are not US assets, and neither, generally, are shares of foreign companies.
              {e.adr_value > 0 ? ` Foreign issuers here (${money(e.adr_value)}) are counted apart; ADRs are unsettled — counting them, ${money(e.estimated_tax_with_adrs)}.` : ''}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">Dividend tax</Typography>
            <Typography variant="h6" fontWeight={700}>{money(dv.tax_now_year)} a year</Typography>
            <Typography variant="body2" color="text.secondary">
              US funds and shares lose 25% of each dividend (the treaty rate with a W-8BEN at the broker; 30% without one).
              An Irish fund pays 15% inside, and its accumulating class pays you nothing to tax until you sell.
              {dv.saving_year > 0 ? <> Switching the funds below would keep about <b>{money(dv.saving_year)}</b> a year, before the deferral.</> : ''}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, height: '100%' }}>
            <Typography variant="overline" color="text.secondary">Losses before 31 December · {h.days_left} days left</Typography>
            <Typography variant="h6" fontWeight={700} color={h.tax_saved_this_year > 0 ? 'success.main' : undefined}>
              {h.unrealized_losses > 0 ? `${money(h.unrealized_losses)} of losses` : 'No losing positions'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A realized loss cancels the same year's gains (and up-to-25% dividends and interest); unused losses carry to
              later years' gains only. Trade Journal gains this year: {money(h.realized_this_year.net)}.
              {h.tax_saved_this_year > 0 ? <> Selling the losers would save about <b>{money(h.tax_saved_this_year)}</b> of this year's tax.</> : ''}
              {' '}Israel computes gains in shekels, so the dollar figure can differ.
            </Typography>
          </Box>
        </Grid>
      </Grid>
      {(withAlt.length > 0 || h.candidates.length > 0) && (
        <Table size="small" sx={{ mt: 1.5, '& td, & th': { px: 1 } }}>
          <TableHead><TableRow>
            <TableCell>Holding</TableCell><TableCell align="right">Value</TableCell><TableCell align="right">Dividend</TableCell>
            <TableCell align="right">Loss to harvest</TableCell><TableCell>Ireland-domiciled alternative</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {d.rows.filter((r: any) => r.alternatives?.length || r.unrealized_loss).map((r: any) => (
              <TableRow key={r.symbol}>
                <TableCell><b>{r.symbol}</b>{r.adr && <Chip size="small" label="foreign issuer" sx={{ ml: 1, height: 18 }} />}</TableCell>
                <TableCell align="right">{money(r.value)}</TableCell>
                <TableCell align="right">{r.dividend_yield_pct != null ? `${r.dividend_yield_pct}%` : '–'}</TableCell>
                <TableCell align="right" sx={{ color: r.unrealized_loss ? 'error.main' : undefined }}>{money(r.unrealized_loss)}</TableCell>
                <TableCell>{r.alternatives?.map((a: any) => `${a.ticker} (${a.listing}, ${a.share_class})`).join(' · ') || '–'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Check the fund's own page and your broker's ticker before buying. Selling a US fund to buy its Irish twin realizes
        its gain (taxed at 25%), so it suits new money, losers, or a year with losses to offset.
      </Typography>
    </Paper>
  );
}
